import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { hasStoredTokens } from '../../utils/auth';
import { useGetUserProfileQuery } from '../../store/slices/apiSlice';
import { restoreAuth } from '../../store/slices/authSlice';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading } = useAppSelector(state => state.auth);
  
  // Check if we have stored tokens but no user data
  const hasTokens = hasStoredTokens();
  const shouldLoadProfile = hasTokens && !isAuthenticated;
  
  // Load user profile if we have tokens but no user data
  const { data: profileData, isLoading: profileLoading, error: profileError } = useGetUserProfileQuery(
    undefined,
    { skip: !shouldLoadProfile }
  );

  // Restore auth when profile is loaded
  useEffect(() => {
    if (profileData?.user && shouldLoadProfile) {
      dispatch(restoreAuth(profileData.user));
    }
  }, [profileData, shouldLoadProfile, dispatch]);

  // Show loading while checking authentication or loading profile
  if (isLoading || (shouldLoadProfile && profileLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4 border-4 border-primary-600 border-t-transparent rounded-full"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated OR has stored tokens (for page refresh)
  const hasAuth = isAuthenticated || hasTokens;

  if (!hasAuth) {
    // Save the attempted location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};